import React, { useMemo } from "react";
import styled from "styled-components";

import Modal from "@fluentui/react/lib/Modal";
import { IconButton } from "@fluentui/react/lib/Button";
import { ResourceStrings } from "../../ResourceStrings";
import { forMinWidth } from "../../theme/breakpoints";
import Flex from "./Flex";
import Button from "./Button";
import { AppPaths, updateVisualStudio, visualStudioLifecycle } from '../../constants/urls';
import { ReleaseAction, ReleaseActionInfo } from "../../store/appState";

export interface OutdatedBuildDialogProps {
    releaseActionInfo: ReleaseActionInfo;
    isOpen: boolean;
    showContinueButton: boolean;
    onClose?: () => void;
    onUpdate?: () => void;
}

const OutdatedBuildBlockDialog = (props: OutdatedBuildDialogProps): JSX.Element => {
    const dialogTitle = useMemo(() => {
        if (props.releaseActionInfo.action === ReleaseAction.Notify) {
            return ResourceStrings.note();
        }
        if (props.releaseActionInfo.action === ReleaseAction.Warn) {
            return ResourceStrings.attention();
        }
        if (props.releaseActionInfo.isPreview) {
            return ResourceStrings.updateRequiredPreview();
        }
        if (props.releaseActionInfo.isFinalMinor){
            return ResourceStrings.mainstreamSupportEnded();
        }
        return ResourceStrings.updateRequired();
    }, [props.releaseActionInfo.version, props.releaseActionInfo.action])

    const dialogText = useMemo(() => {
        if (props.releaseActionInfo.isPreview) {
            if (props.releaseActionInfo.action === ReleaseAction.Notify) {
                return ResourceStrings.outdatedBuildNotifyPreview(props.releaseActionInfo.edition, props.releaseActionInfo.brand);
            }
            if (props.releaseActionInfo.action === ReleaseAction.Warn) {
                return ResourceStrings.outdatedBuildWarnPreview(props.releaseActionInfo.edition, props.releaseActionInfo.brand, props.releaseActionInfo.version);
            }
            return ResourceStrings.outdatedBuildBlockPreview(props.releaseActionInfo.edition, props.releaseActionInfo.brand, props.releaseActionInfo.version);
        }
        else {
            if (props.releaseActionInfo.action === ReleaseAction.Notify) {
                return ResourceStrings.outdatedBuildNotify(props.releaseActionInfo.edition, props.releaseActionInfo.brand, AppPaths.fixes);
            }
            if (props.releaseActionInfo.action === ReleaseAction.Warn) {
                return ResourceStrings.outdatedBuildWarn(props.releaseActionInfo.edition, props.releaseActionInfo.brand, props.releaseActionInfo.version, AppPaths.fixes, visualStudioLifecycle);
            }
            if (props.releaseActionInfo.isFinalMinor){
                return ResourceStrings.outdatedBuildFinalMinorBlock(props.releaseActionInfo.edition, props.releaseActionInfo.brand, AppPaths.fixes, visualStudioLifecycle);
            }
            return ResourceStrings.outdatedBuildBlock(props.releaseActionInfo.edition, props.releaseActionInfo.brand, props.releaseActionInfo.version, AppPaths.fixes, visualStudioLifecycle);
        }
    }, [props.releaseActionInfo.version, props.releaseActionInfo.action]);

    return <Modal
        isOpen={props.isOpen}
        onDismiss={props.onClose}
    >
        <Container role="dialog" aria-label={dialogTitle}>
            <div className="title-bar">
                <IconButton
                    iconProps={{ iconName: 'Cancel' }}
                    onClick={props.onClose}
                />
            </div>
            <div className="content">
                <div className="text heading" aria-label={dialogTitle}>{dialogTitle}</div>
                <div
                    className="text"
                    aria-label={dialogText}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: dialogText }}
                />
                <Flex justifyContent="flex-start" densityHor="normal" className="buttons">
                    <a href={updateVisualStudio} onClick={props.onUpdate}><Button>Update Visual Studio</Button></a>
                    {props.showContinueButton && <Button buttonType="normal" onClick={props.onClose}>Continue to Report</Button>}
                </Flex>
            </div>
        </Container>
    </Modal>
}

export default OutdatedBuildBlockDialog;

const Container = styled.div`
    text-align: left;
    width: auto;
    max-width: 76rem;
    border: 0.1rem solid #3399ff;
    box-shadow: 0.2rem 0.2rem #000000 20% 0.1rem;

    .content {
        padding: 0 5rem 5rem;
    }

    ${forMinWidth.md`
        .content {
            padding: 0 5rem 5rem;
        }
    `}

    .title-bar {
        display: flex;
        justify-content: flex-end;
    }

    .icons {
        display: flex;
        justify-content: space-between;

        &>div + div {
            margin-left: 3.4rem;
        }

        i {
            font-size: 2.8rem;
            line-height: 6.4rem;
            color: var(--color-secondary);
            background-color: rgba(0,0,0, 0.04);
            width: 6rem;
            height: 6rem;
            line-height: 6rem;
            vertical-align: middle;
            border-radius: 50%;
            margin-bottom: 1.5rem;
        }
    }

    .text {
        line-height: 2.4rem;
        margin: 2rem 0;
    }

    .heading {
        font-size: 2.2rem;
        font-weight: 600;
    }

    .sub-heading {
        font-size: 1.4rem;
        margin-bottom: 5.6rem;
    }

    a {
        color: var(--color-on-surface-link);
    }
`;
